import type { BasesHbFields } from '../../lib/hb/types';
import type { Schemas } from '@argzombie/api-client';
import { useState } from 'react';
import { basesDefaults, squadAiDefaults, squadRulesDefaults } from '../../lib/teamsDefaults';
import {
  HEADSHOT_DEFAULTS,
  LOOT_TIER_DEFAULTS,
  type LootTiersDef,
  NOISE_ACTIONS,
  NOISE_METER_DEFAULTS,
  STEALTH_DEFAULTS,
  type SquadAiDef,
  type SquadRulesDef,
} from '../../lib/validate';
import { RaidCombatHudFieldsets, SquadCombatHudFieldsets } from './CombatHudFields';
import { RAID_FOG_DEFAULTS, RAID_LOADOUT_DEFAULTS } from './defaults';
import {
  CheckboxField,
  Fieldset,
  inputCls,
  IssueList,
  ItemIdListEditor,
  ListEditor,
  NumberField,
  NumberInput,
  SelectInput,
  smallBtn,
  StackListEditor,
} from './fields';
import { useContentRefs } from './refs';
import type { FormProps } from './types';
import { staminaDefaults } from '../../lib/skills/defaults';
import type { StaminaDef } from '../../lib/skills/types';
import { RaidWindFieldsets, TierSelectField } from './wind/WindForms';
import { SURVIVAL_WIND_DEFAULTS } from '../../lib/wind/defaults';
import type { SquadAiWind, SquadRulesWind } from '../../lib/teamsDefaults';

/** ADR 0066 adds `raidRules.stamina` and `unarmed.skillId` (typed locally until the client is regenerated). */
type Raid = Omit<Schemas['RaidRulesDef'], 'unarmed'> & {
  unarmed?: Weapon | null;
  stamina?: StaminaDef | null;
};
type Horde = Schemas['HordeRulesDef'];
type Survival = Schemas['SurvivalRulesDef'];
type Weapon = Schemas['WeaponDef'] & { skillId?: string };
type Headshot = Schemas['HeadshotRulesDef'];
type Stealth = Schemas['StealthRulesDef'];
type NoiseMeter = Schemas['NoiseMeterDef'];
type BaseRules = Schemas['BaseRulesDef'] & BasesHbFields;

/** shared-cs `RaidRulesDef.DefaultUnarmed()`: the bare hands of a bundle without `raidRules.unarmed`. */
const DEFAULT_UNARMED: Weapon = {
  skillId: 'blunt',
  damage: 10,
  rangeM: 1.2,
  fireIntervalS: 0.5,
  magazineSize: 0,
  reloadS: 0,
  ammoItemId: null,
  pellets: 1,
  spreadDeg: 0,
  meleeArcDeg: 70,
  durabilityShots: null,
  recoverChance: 0,
  headshotChance: 0,
  // WIND (contract 3.3): bare hands fold combat.windCost.melee.
  windCost: 4,
  windCostPerS: 0,
  recoveryMult: 1,
};

type NumSpec<T> = [key: keyof T & string, label: string, opts?: NumOpts];
/** `min` defaults to 0; `null` = no minimum (a score weight that may be negative). */
type NumOpts = {
  integer?: boolean;
  min?: number | null;
  max?: number;
  hint?: string;
  step?: number;
};

/** Renders plain number fields for a flat object. `prefix` is the issue path prefix. */
function numbers<T extends object>(
  value: T,
  set: (k: keyof T & string, v: number) => void,
  errors: FormProps<T>['errors'],
  specs: NumSpec<T>[],
  prefix = '',
) {
  return specs.map(([k, label, o = {}]) => (
    <NumberField
      key={k}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={o.min === null ? undefined : (o.min ?? 0)}
      max={o.max}
      step={o.step}
      value={(value as Record<string, number | undefined>)[k]}
      issues={errors[prefix + k]}
      onChange={(v) => set(k, v ?? 0)}
    />
  ));
}

const chance: NumOpts = { max: 1, step: 0.05, hint: '0..1' };
/** A score weight: any sign. */
const score: NumOpts = { min: null, step: 1 };
const atLeastOne: NumOpts = { min: 1, step: 0.05 };

export function RaidRulesForm({ value: r, onChange, errors }: FormProps<Raid>) {
  const set = (k: keyof Raid & string, v: number) => onChange({ ...r, [k]: v });
  const horde = r.horde ?? {};
  const setHorde = (k: keyof Horde & string, v: number) =>
    onChange({ ...r, horde: { ...horde, [k]: v } });
  // Bare hands (shared-cs RaidRulesDef.DefaultUnarmed when an older draft has none): melee, never an item.
  const unarmed: Weapon = r.unarmed ?? { ...DEFAULT_UNARMED };
  const setUnarmed = (k: keyof Weapon & string, v: number) =>
    onChange({ ...r, unarmed: { ...unarmed, [k]: v } });
  // Headshots (shared-cs HeadshotRulesDef; absent fields are its defaults).
  const headshot: Headshot = { ...HEADSHOT_DEFAULTS, ...(r.headshot ?? {}) };
  const setHeadshot = (k: keyof Headshot & string, v: number) =>
    onChange({ ...r, headshot: { ...headshot, [k]: v } });
  // Stealth (shared-cs StealthRulesDef; absent fields are its defaults).
  const stealth: Stealth = { ...STEALTH_DEFAULTS, ...(r.stealth ?? {}) };
  const setStealth = (k: keyof Stealth & string, v: number) =>
    onChange({ ...r, stealth: { ...stealth, [k]: v } });
  // The HUD's noise meter (shared-cs NoiseMeterDef, raid overhaul Phase 6; absent fields are its defaults).
  const noiseMeter: NoiseMeter = { ...NOISE_METER_DEFAULTS, ...(r.noiseMeter ?? {}) };
  const setNoiseMeter = (k: keyof NoiseMeter & string, v: number) =>
    onChange({ ...r, noiseMeter: { ...noiseMeter, [k]: v } });
  const nmErrors = errors as FormProps<NoiseMeter>['errors'];
  // The raid loot feed's tiers (shared-cs LootTiersDef, Wave 1; absent fields are its defaults).
  const lootTiers: LootTiersDef = {
    ...LOOT_TIER_DEFAULTS,
    ...((r as { lootTiers?: LootTiersDef | null }).lootTiers ?? {}),
  };
  const setLootTier = (k: keyof LootTiersDef & string, v: number) =>
    onChange({ ...r, lootTiers: { ...lootTiers, [k]: v } } as Raid);
  // Outta-Ten stamina (ADR 0066; shared-cs StaminaDef): the sprint budget, regen and winded. body.sprintStamina / body.staminaRegen scale it.
  const stamina: StaminaDef = { ...staminaDefaults(), ...(r.stamina ?? {}) };
  const setStamina = (k: keyof StaminaDef & string, v: number) =>
    onChange({ ...r, stamina: { ...stamina, [k]: v } });
  const staminaNum = (k: keyof StaminaDef & string, label: string, hint: string) => (
    <NumberField
      key={k}
      label={label}
      hint={hint}
      min={0}
      step={0.05}
      value={stamina[k]}
      issues={errors[`stamina.${k}`]}
      onChange={(v) => setStamina(k, v ?? 0)}
    />
  );
  const { zombies } = useContentRefs();
  const weights = r.zombieSpawnWeights ?? [];
  const total = weights.reduce((s, w) => s + Math.max(0, w.weight ?? 0), 0);

  return (
    <>
      <Fieldset legend="Player">
        {numbers(r, set, errors, [
          [
            'playerMaxHealth',
            'Max health (old)',
            { integer: true, hint: 'Old, kept one content version: Wind replaces it.' },
          ],
          ['crouchSpeedMps', 'Crouch speed (m/s)'],
          ['walkSpeedMps', 'Walk speed (m/s)'],
          ['sprintSpeedMps', 'Sprint speed (m/s)'],
          [
            'footstepIntervalS',
            'Footstep interval (s)',
            { hint: 'Seconds between footstep noises.' },
          ],
          ['playerHearingM', 'Hearing (m)', { hint: 'How far the survivor hears zombie groans.' }],
        ])}
      </Fieldset>
      <RaidWindFieldsets value={r} onChange={onChange} errors={errors} />
      <Fieldset legend="Stamina (ADR 0066, old: Wind replaces it)">
        {staminaNum(
          'baseS',
          'Sprint seconds',
          '1..120 at END 5 (x body.sprintStamina; 0 = cannot sprint).',
        )}
        {staminaNum('regenPerS', 'Regen per second', 'Above 0, at most 10 (x body.staminaRegen).')}
        {staminaNum('windedS', 'Winded seconds', '0..10 after the budget runs out.')}
        {staminaNum('windedSpeedMult', 'Winded speed mult', '0.1..1.')}
        {staminaNum('windedSpreadMult', 'Winded spread mult', '1..3.')}
        {staminaNum('windedAcquireMult', 'Winded acquire mult', '1..3.')}
      </Fieldset>
      <Fieldset legend="Searching and doors">
        {numbers(r, set, errors, [
          ['searchSeconds', 'Search time (s)'],
          ['pryToolSearchMultiplier', 'Pry tool search multiplier', { step: 0.05 }],
          ['lockedDoorChance', 'Locked door chance', chance],
          ['forceDoorSeconds', 'Force door (s)', { hint: 'A pry tool halves it.' }],
          ['lockpickSeconds', 'Lockpick (s)'],
          ['openDoorChance', 'Open door chance', chance],
          ['extraDoorChance', 'Extra door chance', chance],
          ['zombiesPerRoomPerDifficulty', 'Zombies per room per difficulty', { step: 0.1 }],
        ])}
      </Fieldset>
      <Fieldset legend="Loadout">
        {numbers({ ...RAID_LOADOUT_DEFAULTS, ...r }, set, errors, [
          [
            'swapSeconds',
            'Swap time (s)',
            { step: 0.05, hint: 'Switching between the two loadout slots.' },
          ],
          [
            'rummageSeconds',
            'Rummage time (s)',
            { step: 0.1, hint: 'Pulling another weapon from the pack mid-raid.' },
          ],
        ])}
      </Fieldset>
      <Fieldset legend="Fog of war">
        {numbers({ ...RAID_FOG_DEFAULTS, ...r }, set, errors, [
          [
            'visionRadiusM',
            'Vision radius (m)',
            {
              step: 0.5,
              hint: 'How far each survivor sees, all round (1 to 30). Walls and closed doors block.',
            },
          ],
          [
            'ghostMarkerSeconds',
            'Last-seen marker (s)',
            {
              step: 0.5,
              hint: 'How long a pencil marker stays where a zombie went out of sight. 0 = none.',
            },
          ],
        ])}
      </Fieldset>
      <RaidCombatHudFieldsets value={r} onChange={onChange} errors={errors} />
      <Fieldset legend="Loot feed tiers">
        {numbers(
          lootTiers,
          setLootTier,
          errors as FormProps<LootTiersDef>['errors'],
          [
            [
              'good',
              'Good from value',
              { integer: true, min: 1, hint: 'Value per unit; GOOD in cyan.' },
            ],
            [
              'rare',
              'Rare from value',
              { integer: true, min: 1, hint: 'RARE! in yellow, with a burst.' },
            ],
            [
              'prize',
              'Prize from value',
              {
                integer: true,
                min: 1,
                hint: 'PRIZE! in red. A weapon or armor upgrade is always at least Rare.',
              },
            ],
          ],
          'lootTiers.',
        )}
        <IssueList issues={errors.lootTiers} />
      </Fieldset>
      <Fieldset legend="Bare hands (no weapon in the pack)">
        {numbers(
          unarmed,
          setUnarmed,
          errors as FormProps<Weapon>['errors'],
          [
            ['damage', 'Damage', { integer: true }],
            ['rangeM', 'Range (m)', { step: 0.1 }],
            ['fireIntervalS', 'Swing interval (s)', { step: 0.05 }],
            ['meleeArcDeg', 'Arc (degrees)'],
            ['headshotChance', 'Headshot chance', chance],
            ['windCost', 'Wind cost', { max: 50, step: 0.25, hint: 'WIND: per swing, 0..50.' }],
          ],
          'unarmed.',
        )}
        {numbers(r, set, errors, [
          ['unarmedNoiseRadiusM', 'Hit noise (m)', { hint: 'A swing that misses is silent.' }],
        ])}
        <p className="text-xs text-bruise sm:col-span-2">
          Weapon skill: <span className="font-mono">{unarmed.skillId || '(none)'}</span> (ADR 0066:
          bare hands train and use Blunt).
        </p>
        <IssueList issues={errors['unarmed.skillId']} />
      </Fieldset>
      <Fieldset legend="Zombie spawn weights">
        <ListEditor
          label={`Spawn weights (total ${total})`}
          what="zombie"
          items={weights}
          issues={errors.zombieSpawnWeights}
          onChange={(next) => onChange({ ...r, zombieSpawnWeights: next })}
          make={() => ({ zombieId: '', weight: 1 })}
          row={(w, j, setRow) => (
            <div className="grid grid-cols-[minmax(0,1fr)_5rem_3.5rem] items-start gap-2">
              <div>
                <SelectInput
                  label={`Spawn weight ${j + 1} zombie`}
                  value={w.zombieId}
                  options={zombies.map((z) => ({
                    value: z.id ?? '',
                    label: `${z.name || z.id} (${z.id})`,
                  }))}
                  invalid={!!errors[`zombieSpawnWeights.${j}.zombieId`]?.length}
                  onChange={(v) => setRow({ ...w, zombieId: v ?? '' })}
                />
                <IssueList issues={errors[`zombieSpawnWeights.${j}.zombieId`]} />
              </div>
              <div>
                <NumberInput
                  label={`Spawn weight ${j + 1} weight`}
                  integer
                  min={0}
                  value={w.weight}
                  onChange={(v) => setRow({ ...w, weight: v ?? 0 })}
                />
                <IssueList issues={errors[`zombieSpawnWeights.${j}.weight`]} />
              </div>
              <span className="pt-1.5 text-right font-mono text-xs text-bruise">
                {total > 0 ? `${((Math.max(0, w.weight ?? 0) / total) * 100).toFixed(0)}%` : '-'}
              </span>
            </div>
          )}
        />
      </Fieldset>
      <Fieldset legend="Horde">
        {numbers(
          horde,
          setHorde,
          errors as FormProps<Horde>['errors'],
          [
            ['threshold', 'Threshold', { hint: 'Meter value that calls a wave.' }],
            ['fillPerRadiusM', 'Fill per radius meter', { step: 0.1 }],
            [
              'movementFillMultiplier',
              'Movement fill multiplier',
              { step: 0.05, hint: 'Footsteps fill the meter at this share.' },
            ],
            ['decayPerS', 'Decay per second'],
            ['waveBaseSize', 'Wave base size', { integer: true }],
            [
              'waveGrowth',
              'Wave growth',
              { integer: true, hint: 'Added to each wave after the first.' },
            ],
            ['waveCooldownS', 'Wave cooldown (s)'],
            ['maxLiveZombies', 'Max live zombies', { integer: true }],
            [
              'maxFillPerNoise',
              'Max fill per noise',
              { hint: 'The most one noise adds (a 60 m rifle shot counts as 45). 0 = no cap.' },
            ],
          ],
          'horde.',
        )}
      </Fieldset>
      <Fieldset legend="Headshots">
        {numbers(
          headshot,
          setHeadshot,
          errors as FormProps<Headshot>['errors'],
          [
            [
              'steadyS',
              'Steady aim (s)',
              { hint: 'Hold aim on one target this long for a steady shot.' },
            ],
            ['steadyCrouchS', 'Steady aim crouched (s)'],
            [
              'steadyMult',
              'Steady multiplier',
              { step: 0.1, hint: 'Ranged, single-projectile weapons.' },
            ],
            ['unawareMult', 'Unaware target multiplier', { step: 0.1 }],
            ['meleeUnawareMult', 'Unaware target multiplier (melee)', { step: 0.1 }],
            ['staggerMult', 'Staggered target multiplier (melee)', { step: 0.1 }],
            ['staggerWindowS', 'Stagger window (s)', { step: 0.05 }],
            ['sprintMult', 'Sprinting multiplier', { step: 0.05 }],
            [
              'falloffStart',
              'Falloff starts',
              { ...chance, hint: 'Share of the weapon range where falloff begins, 0..1.' },
            ],
            [
              'falloffMin',
              'Falloff at full range',
              { ...chance, hint: 'Chance factor at the weapon range, 0..1.' },
            ],
            ['cap', 'Cap', { ...chance, hint: 'No headshot chance goes above this, 0..1.' }],
          ],
          'headshot.',
        )}
      </Fieldset>
      <Fieldset legend="Stealth">
        {numbers(
          stealth,
          setStealth,
          errors as FormProps<Stealth>['errors'],
          [
            [
              'killRadiusM',
              'Stealth kill radius (m)',
              { step: 0.1, hint: 'A silent weapon kills an unaware zombie this close. Above 0.' },
            ],
            ['cooldownS', 'Stealth kill cooldown (s)', { step: 0.05 }],
            [
              'engagedRadiusM',
              'Engaged radius (m)',
              { hint: 'No auto kill while an alerted zombie is this close.' },
            ],
            [
              'killNoiseM',
              'Stealth kill noise (m)',
              { step: 0.5, hint: 'The body drop, unless the weapon or zombie says more.' },
            ],
            [
              'crouchSightMult',
              'Crouch sight multiplier',
              { step: 0.05, hint: 'Cone range while crouched. Above 0.' },
            ],
            [
              'crouchNoticeMult',
              'Crouch notice multiplier',
              { step: 0.05, hint: 'Notice time while crouched. Above 0.' },
            ],
            [
              'bumpSenseM',
              'Bump sense (m)',
              {
                step: 0.1,
                hint: 'A walking or sprinting survivor this close is felt from any side.',
              },
            ],
            [
              'witnessAwareness',
              'Witness awareness',
              {
                ...chance,
                hint: 'Awareness a zombie gets when its cone sees a stealth kill, 0..1.',
              },
            ],
            [
              'decayPerS',
              'Awareness decay (/s)',
              { step: 0.05, hint: 'Lost per second while out of the cone.' },
            ],
            [
              'suspiciousAt',
              'Suspicious at',
              { ...chance, hint: 'Awareness at which it turns and comes to look, 0..1.' },
            ],
            [
              'alertFlashS',
              'Alert flash (s)',
              { step: 0.1, hint: "How long an alerted zombie's cone flashes red." },
            ],
            [
              'cueRadiusM',
              'Approach cue radius (m)',
              { step: 0.1, hint: 'An eligible target this close gets the approach ring.' },
            ],
            [
              'lungeM',
              'Lunge (m)',
              { step: 0.1, hint: 'How far the survivor lunges during a kill.' },
            ],
            [
              'groupRadiusM',
              'Group radius (m)',
              { step: 0.1, hint: 'Zombies spawned this close together face outward.' },
            ],
            [
              'suspiciousSweepDeg',
              'Suspicious sweep (deg)',
              { max: 180, hint: 'Degrees either side of its heading, 0..180.' },
            ],
            [
              'suspiciousSweepPeriodS',
              'Suspicious sweep period (s)',
              { step: 0.1, hint: 'One full suspicious (and search) sweep. Above 0.' },
            ],
            [
              'searchSweepDeg',
              'Search sweep (deg)',
              { max: 180, hint: 'After losing an alerted target, degrees either side, 0..180.' },
            ],
            [
              'alertedHalfAngleDeg',
              'Alerted half-angle (deg)',
              { max: 180, hint: "A chasing zombie's wide view, above 0 and at most 180." },
            ],
            [
              'alertedProximityM',
              'Alerted proximity (m)',
              { step: 0.1, hint: 'A chasing zombie senses the survivor from any side this close.' },
            ],
          ],
          'stealth.',
        )}
      </Fieldset>
      <Fieldset legend="Noise meter: your noise">
        {numbers(
          noiseMeter,
          setNoiseMeter,
          nmErrors,
          [
            [
              'stepFillPerM',
              'Footstep fill (/m)',
              {
                step: 0.001,
                hint: 'Recent noise a footstep adds per meter of its radius. Above 0.',
              },
            ],
            [
              'fillPerM',
              'Other noise fill (/m)',
              { step: 0.001, hint: 'Per meter of any other noise (shots, blows, doors). Above 0.' },
            ],
            [
              'maxFillPerNoise',
              'Max fill per noise',
              { ...chance, hint: 'The most one noise adds, 0..1.' },
            ],
            [
              'lureFillMult',
              'Lure share',
              { ...chance, hint: "A lure's pulses count this much, 0..1." },
            ],
            [
              'recentTauS',
              'Recent noise decay (s)',
              { step: 0.5, hint: 'Seconds to fall to 37%. Above 0.' },
            ],
            [
              'liveRefM',
              'Live needle full at (m)',
              { hint: 'A noise this loud tops the live needle (log scale). Above 0.' },
            ],
            [
              'liveDecayPerS',
              'Live needle fall (/s)',
              { step: 0.1, hint: 'How fast the needle drops.' },
            ],
            [
              'silenceBrokenAt',
              'Silence broken at',
              { ...chance, hint: 'Past this the horde meter fills faster, 0..1.' },
            ],
            [
              'hordeWakeAt',
              'Horde wakes at',
              {
                ...chance,
                hint: 'Past this the horde meter stops draining. Above the silence mark, 0..1.',
              },
            ],
            [
              'brokenHordeFillMult',
              'Broken silence horde fill x',
              { max: 5, step: 0.05, hint: 'Horde fill multiplier past the silence mark, 0..5.' },
            ],
            [
              'awakeHordeDecayMult',
              'Awake horde drain x',
              { ...chance, hint: 'Horde drain multiplier past the wake mark (0 = none), 0..1.' },
            ],
            [
              'spikeAt',
              'Spike at',
              { step: 0.01, hint: 'One noise adding this much shakes the gauge.' },
            ],
          ],
          'noiseMeter.',
        )}
      </Fieldset>
      <Fieldset legend="Noise meter: what you hear">
        {numbers(
          noiseMeter,
          setNoiseMeter,
          nmErrors,
          [
            [
              'hearRadiusM',
              'Hearing radius (m)',
              { hint: 'Zombies this close count, through walls. Above 0.' },
            ],
            [
              'hearIdleWeight',
              'Idle zombie weight',
              { step: 0.05, hint: 'At your feet, fading to 0 at the radius.' },
            ],
            ['hearMovingWeight', 'Moving zombie weight', { step: 0.05 }],
            [
              'hearSuspiciousWeight',
              'Suspicious zombie weight',
              { step: 0.05, hint: 'Investigating, searching, gawking at a lure.' },
            ],
            [
              'hearAlertedWeight',
              'Alerted zombie weight',
              { step: 0.05, hint: 'Chasing, biting.' },
            ],
            [
              'hearHalfAt',
              'Half full at',
              { step: 0.1, hint: 'Summed weight that fills the channel halfway. Above 0.' },
            ],
            [
              'hearHordeWeight',
              'Horde weight',
              { step: 0.05, hint: 'Added at a full horde meter (x its fill).' },
            ],
            [
              'hearWaveWeight',
              'Wave coming weight',
              { step: 0.1, hint: 'Added while a wave is on its way.' },
            ],
            [
              'hearTauS',
              'Hearing ease (s)',
              { step: 0.1, hint: 'Seconds to move 63% toward what it hears (0 = instant).' },
            ],
            [
              'heartbeatAt',
              'Heartbeat at',
              { ...chance, hint: 'Hearing level that starts the heartbeat, 0..1.' },
            ],
          ],
          'noiseMeter.',
        )}
      </Fieldset>
      <Fieldset legend="Noise meter: standing still">
        {numbers(
          noiseMeter,
          setNoiseMeter,
          nmErrors,
          [
            [
              'stillSpeedMps',
              'Still below (m/s)',
              { step: 0.05, hint: 'Slower than this counts as standing still.' },
            ],
            [
              'stillGraceS',
              'Grace (s)',
              { step: 0.5, hint: 'Standing still this long before zombies drift in (0 = off).' },
            ],
            ['driftEveryS', 'Drift every (s)', { step: 0.5 }],
            [
              'driftRadiusM',
              'Drift radius (m)',
              { hint: 'Only idle, unaware zombies this close drift.' },
            ],
            [
              'driftStepM',
              'Drift step (m)',
              { step: 0.5, hint: "How far each drift moves a zombie's wander spot toward you." },
            ],
            [
              'driftZombies',
              'Zombies per drift',
              { integer: true, max: 10, hint: 'The nearest ones, 0..10 (0 = off).' },
            ],
          ],
          'noiseMeter.',
        )}
      </Fieldset>
    </>
  );
}

export function SurvivalForm({ value: s, onChange, errors }: FormProps<Survival>) {
  const set = (k: keyof Survival & string, v: number) => onChange({ ...s, [k]: v });
  return (
    <>
      <Fieldset legend="Upkeep">
        <ItemIdListEditor
          label="Food items"
          hint="Each counts as one day of food."
          base="foodItemIds"
          value={s.foodItemIds}
          errors={errors}
          onChange={(v) => onChange({ ...s, foodItemIds: v })}
        />
        <ItemIdListEditor
          label="Water items"
          hint="Each counts as one day of water."
          base="waterItemIds"
          value={s.waterItemIds}
          errors={errors}
          onChange={(v) => onChange({ ...s, waterItemIds: v })}
        />
        {numbers(s, set, errors, [
          ['foodPerSurvivorPerDay', 'Food per survivor per day', { integer: true }],
          ['waterPerSurvivorPerDay', 'Water per survivor per day', { integer: true }],
          [
            'starvationDaysToDeath',
            'Starvation days to death',
            { integer: true, hint: 'Consecutive days short before the run ends.' },
          ],
          [
            'starvationDamagePerDay',
            'Starvation damage per day (old)',
            { integer: true, hint: 'Old, kept one content version: starvation debt replaces it.' },
          ],
        ])}
        {numbers({ ...SURVIVAL_WIND_DEFAULTS, ...s }, set, errors, [
          [
            'starvationDebtPerDay',
            'Starvation rest debt per day (winds)',
            { max: 3, step: 0.25, hint: 'WIND: 0..3 winds of rest debt per starving day.' },
          ],
        ])}
      </Fieldset>
      <Fieldset legend="Starter kit">
        <StackListEditor
          label="Starter carried"
          base="starterCarried"
          value={s.starterCarried}
          errors={errors}
          onChange={(v) => onChange({ ...s, starterCarried: v })}
        />
        <StackListEditor
          label="Starter base storage"
          base="starterBase"
          value={s.starterBase}
          errors={errors}
          onChange={(v) => onChange({ ...s, starterBase: v })}
        />
      </Fieldset>
      <Fieldset legend="Distances, timers, recruiting">
        <div className="sm:col-span-2">
          <CheckboxField
            label="Driving lockout enabled"
            hint="Off (ADR 0057, the default): the map never locks for speed and raids never refuse 'driving' for speed alone; an active convoy trip still blocks raids. On: two fast pings above the km/h below lock the map."
            checked={s.drivingLockoutEnabled ?? false}
            onChange={(on) => onChange({ ...s, drivingLockoutEnabled: on })}
          />
        </div>
        {numbers(s, set, errors, [
          ['baseStorageKg', 'Base storage (kg)'],
          ['homeCheckInRadiusM', 'Home check-in radius (m)'],
          ['raidStartRadiusM', 'Raid start radius (m)'],
          ['buildingCooldownHours', 'Building cooldown (h)', { integer: true }],
          ['raidTicketHours', 'Raid ticket lifetime (h)', { integer: true }],
          ['drivingLockoutKmh', 'Driving lockout (km/h)'],
          ['maxRecruits', 'Max recruits', { integer: true }],
          ['recruitTokenMinutes', 'Recruit QR lifetime (min)', { integer: true }],
          ['recruitPairCooldownHours', 'Recruit pair cooldown (h)', { integer: true }],
        ])}
      </Fieldset>
    </>
  );
}

type Noise = Record<string, number>;

/** Radius per action id. Known actions come first; custom ids can be added or removed. */
export function NoiseRadiiForm({ value, onChange, errors }: FormProps<Noise>) {
  const [newKey, setNewKey] = useState('');
  const known: readonly string[] = NOISE_ACTIONS;
  const keys = [...NOISE_ACTIONS, ...Object.keys(value).filter((k) => !known.includes(k))];
  const remove = (k: string) => {
    const next = { ...value };
    delete next[k];
    onChange(next);
  };

  return (
    <Fieldset legend="Noise radius per action (m)">
      {keys.map((k) => (
        <div key={k}>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <NumberField
                label={k}
                min={0}
                value={value[k]}
                nullable
                placeholder="not set"
                issues={errors[k]}
                onChange={(v) => (v == null ? remove(k) : onChange({ ...value, [k]: v }))}
              />
            </div>
            {!known.includes(k) && (
              <button
                type="button"
                className={`${smallBtn} mb-1 text-blam`}
                onClick={() => remove(k)}
              >
                Remove
              </button>
            )}
          </div>
        </div>
      ))}
      <div className="flex items-end gap-2 sm:col-span-2">
        <input
          aria-label="New action id"
          placeholder="new action id"
          className={`${inputCls} max-w-64 font-mono`}
          value={newKey}
          onChange={(e) => setNewKey(e.target.value.trim())}
        />
        <button
          type="button"
          className={smallBtn}
          disabled={!newKey || newKey in value}
          onClick={() => {
            onChange({ ...value, [newKey]: 0 });
            setNewKey('');
          }}
        >
          Add action
        </button>
      </div>
    </Fieldset>
  );
}

/** Homebase, outposts (camps in the data, ADR 0061) and the check-in ledger (ADR 0030): outpost set-up, check-ins, home moves, expiry, caravans. */
export function BasesForm({ value, onChange, errors }: FormProps<BaseRules>) {
  // Absent fields (a draft from before camps, or partial JSON) are the C# defaults, like on the server.
  const r: BaseRules = { ...basesDefaults(), ...value };
  const set = (k: keyof BaseRules & string, v: number) => onChange({ ...r, [k]: v });
  const int = { integer: true };
  return (
    <>
      <Fieldset legend="Outposts">
        {numbers(r, set, errors, [
          [
            'maxActiveCamps',
            'Max active outposts',
            { ...int, hint: 'Besides Home. An old home that became an outpost may go one over.' },
          ],
          ['campMinDistanceFromHomeM', 'Min distance from home (m)'],
          ['campMinDistanceBetweenM', 'Min distance between outposts (m)'],
          ['campDropCooldownHours', 'Set-up cooldown (h)', int],
          [
            'maxCampDropsPer30Days',
            'Max outposts set up per 30 days',
            { ...int, hint: 'Expired and closed outposts count.' },
          ],
          ['dropMaxAccuracyM', 'Max GPS accuracy to set one up (m)', { hint: 'Above 0.' }],
          [
            'dropAfterRelocationMinutes',
            'No set-up after a relocation (min)',
            { ...int, hint: 'Minutes after the anchor relocated.' },
          ],
        ])}
      </Fieldset>
      <Fieldset legend="Automatic drop">
        {numbers(r, set, errors, [
          [
            'autoDropSettleFixes',
            'Settled fixes needed',
            { ...int, hint: 'Qualifying fixes (inside one place, accurate, fresh) in the window.' },
          ],
          [
            'autoDropWindowFixes',
            'Window (fixes)',
            { ...int, hint: 'The last N fixes looked at; a bad one is skipped. At least the fixes needed.' },
          ],
          ['autoDropSettleSeconds', 'Fixes must span (s)'],
          [
            'autoDropMaxAccuracyM',
            'Max GPS accuracy (m)',
            { hint: 'Above 0. The server refuses a rougher automatic drop (poor_gps).' },
          ],
          ['autoDropMaxFixAgeSeconds', 'Max fix age (s)', { hint: 'Above 0.' }],
          ['autoDropBackoffSeconds', 'Wait after a refusal (s)', { hint: 'Doubles each refusal.' }],
          ['autoDropBackoffMaxSeconds', 'Longest wait (s)'],
        ])}
      </Fieldset>
      <Fieldset legend="Check-ins">
        {numbers(r, set, errors, [
          [
            'checkInRadiusM',
            'Check-in radius (m)',
            { hint: "0 = the survival rules' home check-in radius." },
          ],
          ['checkInMinGapHours', 'Min gap between counted check-ins (h)', { hint: 'Per base.' }],
        ])}
      </Fieldset>
      <Fieldset legend="Home moves and promotion">
        <div className="sm:col-span-2">
          <CheckboxField
            label="Home follows use"
            hint="ADR 0049. Off (ADR 0061): Home moves only by MAKE THIS MY HOME at an outpost, once per home move cooldown. On: an outpost used more than Home becomes Home by itself."
            checked={r.autoHomeEnabled ?? true}
            onChange={(on) => onChange({ ...r, autoHomeEnabled: on })}
          />
        </div>
        {numbers(r, set, errors, [
          [
            'homeMoveCooldownDays',
            'Home move cooldown (days)',
            { ...int, hint: 'Days between two MAKE THIS MY HOME swaps (and after setting Home).' },
          ],
          [
            'initialHomeGraceHours',
            'Initial home grace (h)',
            { ...int, hint: 'The first home can be moved freely this long after it was set.' },
          ],
          ['promoteMinCheckIns', 'Promote: min counted check-ins', { ...int, min: 1 }],
          [
            'promoteMinDistinctDays',
            'Promote: min distinct days',
            { ...int, min: 1, hint: 'UTC days; at most the check-ins.' },
          ],
          ['promoteMinSpanHours', 'Promote: min span (h)', { hint: 'First to last check-in.' }],
          [
            'promoteMinSessions',
            'Promote: min sessions',
            { ...int, min: 1, hint: 'Unbroken ping streams.' },
          ],
        ])}
      </Fieldset>
      <Fieldset legend="Expiry and regen">
        {numbers(r, set, errors, [
          [
            'campExpiryDays',
            'Outpost expiry (days)',
            { ...int, min: 1, hint: 'With no counted check-in.' },
          ],
          ['campExpiryWarningHours', 'Expiry warning (h)', int],
          [
            'campRegenMult',
            'Outpost regen multiplier',
            { ...chance, hint: '0..1 of the home rate (no infirmary).' },
          ],
        ])}
      </Fieldset>
      <Fieldset legend="Home follows use (ADR 0049)">
        {numbers(r, set, errors, [
          [
            'autoHomeWindowDays',
            'Use window (days)',
            {
              ...int,
              hint: 'Use-days (a counted check-in or setting up the outpost) are counted over this rolling window.',
            },
          ],
          [
            'autoHomeLeadDays',
            'Lead over home (days)',
            {
              ...int,
              hint: 'An outpost becomes home with at least this many more use-days than home. Below the window.',
            },
          ],
          [
            'autoHomeMinUseDays',
            'Min use-days',
            {
              ...int,
              hint: 'An outpost becomes home only with at least this many use-days. At most the window.',
            },
          ],
          [
            'autoHomeFlagQuietHours',
            'Quiet after a flagged ping (h)',
            {
              ...int,
              hint: 'No flip while an implausible ping arrived in the last this many hours.',
            },
          ],
        ])}
      </Fieldset>
      <Fieldset legend="Caravans">
        <div className="sm:col-span-2">
          <CheckboxField
            label="Caravans enabled"
            hint="Off (ADR 0048): dropping gear at an outpost lands it in Inventory at once. On: the haul travels home by caravan."
            checked={r.caravansEnabled ?? true}
            onChange={(on) => onChange({ ...r, caravansEnabled: on })}
          />
        </div>
        {numbers(r, set, errors, [
          ['caravanMinHours', 'Shortest trip (h)'],
          ['caravanMaxHours', 'Longest trip (h)', { hint: 'At least the shortest.' }],
          ['caravanHoursPer100Km', 'Hours per 100 km', { hint: 'Outpost to home distance.' }],
        ])}
      </Fieldset>
    </>
  );
}

/** Squad raids (ADR 0027): costs per extra member, downed and revive, and the AI teammates' tuning. */
type SquadW = SquadRulesDef & SquadRulesWind;
type SquadAiW = SquadAiDef & SquadAiWind;

export function SquadRulesForm({ value, onChange, errors }: FormProps<SquadW>) {
  // Absent fields (a draft from before squads, or partial JSON) are the C# defaults, like on the server.
  const s: SquadW = { ...squadRulesDefaults(), ...value };
  const ai: SquadAiW = { ...squadAiDefaults(), ...(value.ai ?? {}) };
  const setWind = (k: keyof SquadRulesWind & string, v: number | string) =>
    onChange({ ...s, [k]: v });
  const setAiWind = (k: keyof SquadAiWind & string, v: number | string) =>
    onChange({ ...s, ai: { ...ai, [k]: v } });
  const set = (k: keyof SquadW & string, v: number) => onChange({ ...s, [k]: v });
  const setAi = (k: keyof SquadAiW & string, v: number) =>
    onChange({ ...s, ai: { ...ai, [k]: v } });
  const extra = 'Per extra member (every member after the first).';

  return (
    <>
      <Fieldset legend="Costs and scaling">
        {numbers(s, set, errors, [
          ['raidEnergyPerExtraMember', 'Raid energy per extra member', { hint: extra }],
          ['inPersonEnergyPerExtraMember', 'In-person energy per extra member', { hint: extra }],
          ['travelEnergyPerKmPerExtraMember', 'Travel energy per km per extra member'],
          [
            'zombieMultPerExtraMember',
            'Zombie multiplier per extra member',
            { step: 0.05, hint: 'Room zombies × (1 + this × extra members).' },
          ],
          [
            'maxLiveZombiesPerExtraMember',
            'Max live zombies per extra member',
            { integer: true, hint: 'Added to the horde cap.' },
          ],
        ])}
        <NumberField
          label="Horde threshold per extra member"
          hint="-0.5..1. Horde threshold × (1 + this × extra members); 0 = none."
          min={-0.5}
          max={1}
          step={0.05}
          value={s.hordeThresholdPerExtraMember}
          issues={errors.hordeThresholdPerExtraMember}
          onChange={(v) => set('hordeThresholdPerExtraMember', v ?? 0)}
        />
        {numbers(s, set, errors, [
          [
            'rationsPerExtraMember',
            'Rations per extra member',
            { integer: true, hint: '0 = off (daily upkeep already feeds everyone).' },
          ],
        ])}
        <ItemIdListEditor
          label="Ration items"
          hint="Food a squad raid may charge."
          base="rationItemIds"
          value={s.rationItemIds}
          errors={errors}
          onChange={(v) => onChange({ ...s, rationItemIds: v })}
        />
      </Fieldset>
      <Fieldset legend="Rooms for big squads">
        {numbers(s, set, errors, [
          [
            'largeRoomsAtSquadSize',
            'Big rooms from squad size',
            {
              integer: true,
              max: 4,
              hint: '0 = never, else 2..4. A squad this big or bigger gets roomier interiors (solo stays the same).',
            },
          ],
          [
            'largeRoomMinSide',
            'Big room min side (m)',
            { integer: true, min: 4, max: 8, hint: '4..8. The base is 4.' },
          ],
          [
            'largeRoomAverageArea',
            'Big room average area (m²)',
            { integer: true, max: 100, hint: 'The min side squared up to 100. The base is 28.' },
          ],
        ])}
      </Fieldset>
      <Fieldset legend="Downed and revive">
        {numbers(s, set, errors, [
          ['downedBleedOutS', 'Bleed out (s)', { hint: 'A downed teammate dies after this.' }],
          [
            'downedBitesToDie',
            'Bites to die while downed (old)',
            {
              integer: true,
              hint: 'Old, kept one content version: wounds to die while downed replaces it.',
            },
          ],
          ['reviveSeconds', 'Revive hold (s)'],
          ['reviveRangeM', 'Revive range (m)', { step: 0.1 }],
          [
            'reviveHealthFrac',
            'Revive health fraction (old)',
            {
              max: 1,
              step: 0.05,
              hint: 'Old, kept one content version: revive tier and wind replace it.',
            },
          ],
          [
            'revivesPerMember',
            'Revives per member',
            { integer: true, max: 1, hint: '0 or 1 per raid; the next down is death.' },
          ],
        ])}
      </Fieldset>
      <Fieldset legend="Wind: downed, revive and the AI (WIND)">
        <NumberField
          label="Wounds to die while downed"
          hint="1..5. 1 = a grab while down kills."
          integer
          min={1}
          max={5}
          value={s.downedWoundsToDie}
          issues={errors.downedWoundsToDie}
          onChange={(v) => setWind('downedWoundsToDie', v ?? 1)}
        />
        <TierSelectField
          label="Revive tier"
          hint="A revived teammate gets up at this tier..."
          value={s.reviveTier}
          issues={errors.reviveTier}
          onChange={(v) => setWind('reviveTier', v)}
        />
        <NumberField
          label="Revive wind fraction"
          hint="0..1. ...with this share of that tier's tank."
          min={0}
          max={1}
          step={0.05}
          value={s.reviveWindFrac}
          issues={errors.reviveWindFrac}
          onChange={(v) => setWind('reviveWindFrac', v ?? 0)}
        />
        <TierSelectField
          label="AI falls back at tier"
          hint="A following teammate at this tier or deeper falls back (replaces the retreat health fraction)."
          value={ai.fallBackAtTier}
          issues={errors['ai.fallBackAtTier']}
          onChange={(v) => setAiWind('fallBackAtTier', v)}
        />
        <TierSelectField
          label="AI clutch at player tier"
          hint="The clutch window opens when the player is at this tier or deeper, or grabbed (replaces the clutch player health fraction)."
          value={ai.clutchAtTier}
          issues={errors['ai.clutchAtTier']}
          onChange={(v) => setAiWind('clutchAtTier', v)}
        />
        <NumberField
          label="AI hurt window (s)"
          hint="1..60. How long a hit counts as recent for the AI."
          min={1}
          max={60}
          step={0.5}
          value={ai.hurtWindowS}
          issues={errors['ai.hurtWindowS']}
          onChange={(v) => setAiWind('hurtWindowS', v ?? 10)}
        />
      </Fieldset>
      <SquadCombatHudFieldsets value={s} onChange={onChange} errors={errors} />
      <Fieldset legend="AI teammates">
        <IssueList issues={errors.ai} />
        {numbers(
          ai,
          setAi,
          errors,
          [
            ['thinkHz', 'Think rate (Hz)', { hint: 'Brain updates per second.' }],
            ['acquireMinS', 'Acquire min (s)', { step: 0.05 }],
            ['acquireMaxS', 'Acquire max (s)', { step: 0.05 }],
            [
              'firstShotWindowS',
              'First shot window (s)',
              { step: 0.05, hint: "Extra delay on a zombie in the player's view." },
            ],
            [
              'fireIntervalMult',
              'Fire interval multiplier',
              { step: 0.05, hint: "The weapon's fire interval × this." },
            ],
            ['hitChanceNear', 'Hit chance near', chance],
            ['nearM', 'Near (m)'],
            ['hitChanceFar', 'Hit chance far', chance],
            ['farM', 'Far (m)'],
            ['headshotChance', 'Headshot chance', chance],
            [
              'retreatHealthFrac',
              'Retreat health fraction (old)',
              { ...chance, hint: 'Old, kept one version: AI falls back at tier replaces it.' },
            ],
            ['killShareSoftCap', 'Kill share soft cap', chance],
            ['killShareHardCap', 'Kill share hard cap', chance],
            [
              'zombieTargetWeight',
              'Zombie target weight',
              { step: 0.05, hint: "Against the player's 1.0." },
            ],
            ['downedTargetWeight', 'Downed target weight', { step: 0.05 }],
            ['biteGraceS', 'Bite grace (s)', { step: 0.1 }],
            [
              'noiseTaxPerFollower',
              'Noise tax per follower',
              { step: 0.05, hint: 'Footstep radius × (1 + this × following teammates).' },
            ],
          ],
          'ai.',
        )}
      </Fieldset>
      <Fieldset legend="AI target choice">
        {numbers(
          ai,
          setAi,
          errors,
          [
            [
              'scoreChasingPlayer',
              'Score: chasing the player',
              { ...score, hint: 'A zombie chasing or biting the body the player drives.' },
            ],
            [
              'scoreNearSquad',
              'Score: near the squad',
              { ...score, hint: 'A zombie within "near squad" of any squad body.' },
            ],
            [
              'nearSquadM',
              'Near squad (m)',
              { step: 0.1, hint: "Also how close skips the player's first shot window." },
            ],
            [
              'scoreInSector',
              'Score: in my sector',
              { ...score, hint: "This teammate's watch arc." },
            ],
            ['scoreRunner', 'Score: runner', score],
            [
              'scoreBrute',
              'Score: brute',
              { ...score, hint: 'Unless ordered: the player decides on brutes.' },
            ],
            [
              'scorePlayersTarget',
              "Score: the player's target",
              { ...score, hint: "Farther than the min below from the player: don't steal kills." },
            ],
            ['playersTargetMinM', "Player's target min (m)", { step: 0.1 }],
            [
              'scoreShared',
              'Score: shared target',
              { ...score, hint: 'Another teammate is already on it.' },
            ],
            ['scorePerMeter', 'Score per meter', { ...score, step: 0.5 }],
          ],
          'ai.',
        )}
      </Fieldset>
      <Fieldset legend="AI fire discipline">
        {numbers(
          ai,
          setAi,
          errors,
          [
            ['meleeHitChance', 'Melee hit chance', chance],
            [
              'clutchHitChance',
              'Clutch hit chance',
              { ...chance, hint: '0..1. In the clutch window (the player is in danger).' },
            ],
            ['clutchShots', 'Clutch shots', { integer: true, hint: 'Per clutch window.' }],
            ['clutchCooldownS', 'Clutch cooldown (s)', { hint: 'Per teammate.' }],
            [
              'clutchPlayerHealthFrac',
              'Clutch player health fraction (old)',
              { ...chance, hint: 'Old, kept one version: AI clutch at player tier replaces it...' },
            ],
            [
              'clutchRangeM',
              'Clutch range (m)',
              { step: 0.1, hint: '...or for a zombie within this of the player.' },
            ],
            [
              'lineOfFireRadiusM',
              'Line of fire radius (m)',
              { step: 0.05, hint: 'Never fire with a squad body this close to the shot line...' },
            ],
            [
              'lineOfFirePastM',
              'Line of fire past target (m)',
              { step: 0.1, hint: '...or this far past the target.' },
            ],
            [
              'reloadBelowFrac',
              'Reload below fraction',
              { ...chance, hint: '0..1 of the magazine, once idle for the time below.' },
            ],
            ['reloadIdleS', 'Reload idle (s)', { step: 0.1 }],
          ],
          'ai.',
        )}
      </Fieldset>
      <Fieldset legend="AI kill-share governor">
        {numbers(
          ai,
          setAi,
          errors,
          [
            [
              'killShareWindowS',
              'Kill share window (s)',
              { hint: 'Rolling window for the soft cap.' },
            ],
            [
              'killShareMinKills',
              'Kill share min kills',
              { integer: true, hint: 'Shares count over at least this many kills.' },
            ],
            [
              'softCapHitMult',
              'Soft cap hit multiplier',
              { ...chance, hint: '0..1. Over the soft cap: hit chance × this...' },
            ],
            [
              'softCapAcquireAddS',
              'Soft cap acquire add (s)',
              { step: 0.05, hint: '...and this much longer to acquire.' },
            ],
            [
              'hardCapRangeM',
              'Hard cap range (m)',
              { step: 0.1, hint: 'Over the hard cap: only clutch shots or zombies this close.' },
            ],
            [
              'struggleShare',
              'Struggle share',
              { ...chance, hint: '0..1. Under this share while the player is hurt...' },
            ],
            [
              'struggleHitMult',
              'Struggle hit multiplier',
              { step: 0.05, hint: '...hit chance × this.' },
            ],
            [
              'struggleWindowS',
              'Struggle window (s)',
              { hint: 'The player took damage within this.' },
            ],
          ],
          'ai.',
        )}
      </Fieldset>
      <Fieldset legend="AI fire mode">
        {numbers(
          ai,
          setAi,
          errors,
          [
            [
              'weaponsFreeNoiseM',
              'Weapons Free noise (m)',
              {
                hint: 'A player shot louder than this sets Weapons Free (a suppressed pistol at 12 m stays quiet).',
              },
            ],
            [
              'stealthRevertS',
              'Stealth revert (s)',
              { hint: "Unnoticed this long: back to the player's last choice." },
            ],
          ],
          'ai.',
        )}
      </Fieldset>
      <Fieldset legend="AI formations">
        {numbers(
          ai,
          setAi,
          errors,
          [
            ['fileSpacingM', 'FILE spacing (m)', { step: 0.1, hint: 'Walking.' }],
            ['fileSprintSpacingM', 'FILE sprint spacing (m)', { step: 0.1 }],
            ['fileCrouchSpacingM', 'FILE crouch spacing (m)', { step: 0.1 }],
            ['fileFirstM', 'FILE first follower (m)', { step: 0.1, hint: 'Behind the player.' }],
            [
              'fanBearingDeg',
              'FAN bearing (°)',
              {
                min: 90,
                max: 180,
                hint: "90..180. Slots A and B either side of the player's heading.",
              },
            ],
            ['fanSideM', 'FAN side distance (m)', { step: 0.1 }],
            ['fanRearM', 'FAN rear distance (m)', { step: 0.1, hint: 'Slot C, straight behind.' }],
            [
              'narrowWidthM',
              'Narrow width (m)',
              { step: 0.1, hint: 'FILE when the free width is under this...' },
            ],
            ['doorwayM', 'Doorway (m)', { step: 0.1, hint: '...or a doorway is this close.' }],
            [
              'combatTighten',
              'Combat tighten',
              { max: 1, step: 0.05, hint: 'Above 0, at most 1: slot distances × this in combat.' },
            ],
            ['slotToleranceM', 'Slot tolerance (m)', { step: 0.05 }],
            [
              'slotSwitchGain',
              'Slot switch gain',
              { ...chance, hint: '0..1. Reassign slots only when this much better.' },
            ],
            [
              'noGoHalfAngleDeg',
              'No-go half angle (°)',
              { max: 90, hint: "0..90. Nobody stands in the player's forward cone..." },
            ],
            ['noGoRangeM', 'No-go range (m)', { hint: '...out to this range.' }],
            ['minSpacingM', 'Min spacing (m)', { step: 0.1 }],
          ],
          'ai.',
        )}
      </Fieldset>
      <Fieldset legend="AI speed and catch-up teleport">
        {numbers(
          ai,
          setAi,
          errors,
          [
            [
              'matchNearM',
              'Match speed near (m)',
              { step: 0.1, hint: "Within this of its slot: the player's speed." },
            ],
            [
              'catchUpFarM',
              'Catch up far (m)',
              { step: 0.1, hint: 'At least "match speed near"; beyond it, sprint.' },
            ],
            ['catchUpMult', 'Catch-up multiplier', { ...atLeastOne, hint: 'At least 1.' }],
            [
              'sprintCatchUpMult',
              'Sprint catch-up multiplier',
              { ...atLeastOne, hint: 'At least 1. Sprint speed × this.' },
            ],
            [
              'crouchCatchUpMult',
              'Crouch catch-up multiplier',
              { ...atLeastOne, hint: 'At least 1. Hold Fire: crouch speed × this at most.' },
            ],
            [
              'teleportPathM',
              'Teleport path distance (m)',
              { hint: 'This far (by path) from its slot...' },
            ],
            ['teleportAfterS', 'Teleport after (s)', { step: 0.5, hint: '...for this long.' }],
            ['teleportCooldownS', 'Teleport cooldown (s)'],
            [
              'teleportBackMinM',
              'Teleport back min (m)',
              { step: 0.5, hint: "Lands on the player's trail this far back..." },
            ],
            [
              'teleportBackMaxM',
              'Teleport back max (m)',
              { step: 0.5, hint: '...up to this (at least the min).' },
            ],
          ],
          'ai.',
        )}
      </Fieldset>
      <Fieldset legend="AI orders, stealth and revives">
        {numbers(
          ai,
          setAi,
          errors,
          [
            ['attackLostS', 'Attack lost (s)', { hint: 'An Attack order ends after this.' }],
            [
              'regroupIgnoreM',
              'Regroup ignore (m)',
              { step: 0.1, hint: 'On Regroup, ignore zombies farther than this.' },
            ],
            [
              'invisibleFollowM',
              'Invisible follow (m)',
              { hint: 'Hold Fire: unseen and unheard this close to the player.' },
            ],
            [
              'rescueCooldownS',
              'Rescue cooldown (s)',
              { hint: 'Hold Fire: kill a zombie biting the player once per this.' },
            ],
            [
              'autoReviveClearM',
              'Auto revive clear (m)',
              { hint: 'Weapons Free: revive when no zombie is this close.' },
            ],
          ],
          'ai.',
        )}
      </Fieldset>
      <Fieldset legend="AI fall-back, stack and breach">
        {numbers(
          ai,
          setAi,
          errors,
          [
            [
              'fallBackFightM',
              'Fall-back fight range (m)',
              {
                step: 0.1,
                hint: 'Below the retreat health: the rearmost slot, fighting only this close.',
              },
            ],
            [
              'stackWallOffsetM',
              'Stack wall offset (m)',
              { step: 0.05, hint: "Off the door's wall." },
            ],
            [
              'stackFirstM',
              'Stack first (m)',
              { step: 0.05, hint: 'The first man from the jamb...' },
            ],
            ['stackSpacingM', 'Stack spacing (m)', { step: 0.05, hint: '...and each next one.' }],
            [
              'entryIntervalS',
              'Entry interval (s)',
              { step: 0.05, hint: 'Bodies through the door this far apart.' },
            ],
            [
              'entryHoldS',
              'Entry hold (s)',
              { step: 0.05, hint: 'The third man holds the doorway this long.' },
            ],
            [
              'entryPointMinM',
              'Entry point min (m)',
              { step: 0.1, hint: 'Along the inside wall from the door...' },
            ],
            [
              'entryPointMaxM',
              'Entry point max (m)',
              { step: 0.1, hint: '...up to this (at least the min).' },
            ],
            ['entryTimeoutS', 'Entry timeout (s)', { step: 0.5, hint: 'Not there by then: hold.' }],
            [
              'clearQuietS',
              'Clear quiet (s)',
              { step: 0.1, hint: '"Clear!" after this long with nothing seen.' },
            ],
            [
              'openNoiseM',
              'Open noise (m)',
              { step: 0.5, hint: 'Opening the stacked door quietly.' },
            ],
            [
              'kickNoiseM',
              'Kick noise (m)',
              { step: 0.5, hint: 'Kicking it in (forces a locked door)...' },
            ],
            [
              'kickStaggerRadiusM',
              'Kick stagger radius (m)',
              { step: 0.1, hint: '...staggers zombies this close...' },
            ],
            ['kickStaggerS', 'Kick stagger (s)', { step: 0.1, hint: '...for this long.' }],
          ],
          'ai.',
        )}
      </Fieldset>
      <Fieldset legend="AI barks and edge arrows">
        {numbers(
          ai,
          setAi,
          errors,
          [
            ['barkGapS', 'Bark gap (s)', { step: 0.1, hint: 'At most one squad bark per this.' }],
            [
              'plainAcks',
              'Plain acks',
              { integer: true, hint: 'Acks shorten to one word after this many.' },
            ],
            ['contactBarkCooldownS', 'Contact bark cooldown (s)', { hint: 'Per teammate.' }],
            ['reloadBarkCooldownS', 'Reload bark cooldown (s)', { hint: 'Per teammate.' }],
            ['killBarkChance', 'Kill bark chance', { ...chance, hint: '0..1. "Got one."...' }],
            ['killBarkCooldownS', 'Kill bark cooldown (s)', { hint: '...at most once per this.' }],
            [
              'idleBarkCooldownS',
              'Idle bark cooldown (s)',
              { hint: 'Ambient lines, only when quiet.' },
            ],
            [
              'peekS',
              'Edge arrow peek (s)',
              { step: 0.1, hint: 'Tapping an edge arrow peeks the camera this long.' },
            ],
          ],
          'ai.',
        )}
      </Fieldset>
      <Fieldset legend="Ordering time (slow motion)">
        {numbers(
          ai,
          setAi,
          errors,
          [
            [
              'orderTimeScale',
              'Order time scale',
              { max: 1, step: 0.05, hint: 'Above 0, at most 1: time scale while ordering.' },
            ],
            [
              'orderSlowMoCapS',
              'Slow motion cap (s)',
              { step: 0.5, hint: 'Real seconds per gesture, at most.' },
            ],
            ['orderSlowMoRampS', 'Slow motion ramp (s)', { step: 0.05 }],
            ['orderSlowMoCooldownS', 'Slow motion cooldown (s)', { step: 0.1 }],
          ],
          'ai.',
        )}
      </Fieldset>
      <Fieldset legend="Zombies vs the squad">
        {numbers(
          ai,
          setAi,
          errors,
          [
            [
              'stickinessM',
              'Target stickiness (m)',
              { step: 0.1, hint: "A zombie's current target counts this much closer." },
            ],
            [
              'teammateAttackers',
              'Attackers per teammate',
              { integer: true, min: 1, hint: 'At least 1.' },
            ],
            [
              'playerAttackersCovered',
              'Player attackers (covered)',
              { integer: true, min: 1, hint: 'At least 1. A teammate is near.' },
            ],
            [
              'playerAttackersAlone',
              'Player attackers (alone)',
              { integer: true, min: 1, hint: 'At least 1.' },
            ],
            ['teammateNearM', 'Teammate near (m)', { hint: 'Counts as "covered".' }],
            [
              'holdOffM',
              'Hold off (m)',
              { step: 0.1, hint: 'A zombie without an attack token waits this far off.' },
            ],
            [
              'offscreenWindUpS',
              'Off-screen wind-up (s)',
              { step: 0.05, hint: 'Extra, biting the player from off-screen.' },
            ],
            [
              'biteStaggerM',
              'Bite stagger (m)',
              { step: 0.1, hint: 'A zombie that bit a teammate staggers back.' },
            ],
          ],
          'ai.',
        )}
      </Fieldset>
    </>
  );
}
